import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useMemo, useState } from 'react'
import { NavLink, useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Tooltip } from '@/components/ui/tooltip'
import { useAuth } from '@/lib/auth/AuthProvider'
import { cn } from '@/lib/utils'
import { visibleNav, type NavGroup, type NavItem } from './nav'

const GROUPS_KEY = 'cp.nav.groups'

function matchPath(pathname: string, to: string) {
  if (to === '/') return pathname === '/'
  return pathname === to || pathname.startsWith(`${to}/`)
}

function itemActive(item: NavItem, pathname: string) {
  return item.match ? item.match(pathname) : matchPath(pathname, item.to)
}

function groupHasActive(group: NavGroup, pathname: string) {
  return group.items.some((i) => itemActive(i, pathname))
}

function readOpenGroup(): string | null {
  try {
    const raw = localStorage.getItem(GROUPS_KEY)
    const parsed = raw ? (JSON.parse(raw) as unknown) : null
    if (typeof parsed === 'string') return parsed
    if (Array.isArray(parsed) && typeof parsed[0] === 'string') return parsed[0]
  } catch {
    /* yok say */
  }
  return null
}

function persistOpenGroup(key: string | null) {
  try {
    localStorage.setItem(GROUPS_KEY, JSON.stringify(key))
  } catch {
    /* yok say */
  }
}

export function Brand({ collapsed, inverted }: { collapsed?: boolean; inverted?: boolean }) {
  const { t } = useTranslation()
  return (
    <div
      className={cn(
        'flex h-14 shrink-0 items-center gap-3 border-b px-4',
        inverted ? 'border-sidebar-border' : 'border-sidebar-border/80',
        collapsed && 'justify-center px-0',
      )}
    >
      <div className={cn('flex size-9 shrink-0 items-center justify-center', inverted && 'rounded-xl bg-white p-1 shadow-sm ring-1 ring-black/5 dark:ring-0')}>
        <img src="/urunver.png" alt="Ürünver" className="size-full object-contain" />
      </div>
      {collapsed ? null : (
        <div className="min-w-0 leading-tight">
          <div className={cn('truncate text-[15px] font-semibold tracking-tight', 'text-foreground')}>{t('app.name')}</div>
          <div className={cn('truncate text-[11px] font-medium', inverted ? 'text-muted-foreground' : 'text-muted-foreground/80')}>{t('app.product')}</div>
        </div>
      )}
    </div>
  )
}

function PopoverItemLink({ item, active, onNavigate }: { item: NavItem; active: boolean; onNavigate?: () => void }) {
  const { t } = useTranslation()
  const Icon = item.icon
  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      onClick={onNavigate}
      className={cn(
        'group flex items-center gap-2.5 rounded-lg px-2.5 py-[7px] text-[13px] font-medium transition-colors',
        active
          ? 'bg-brand-blue/10 font-semibold text-brand-navy dark:bg-brand-sky/15 dark:text-brand-sky'
          : 'text-foreground/70 hover:bg-muted hover:text-foreground',
      )}
    >
      <Icon className={cn('size-[15px] shrink-0', active ? 'text-brand-orange' : 'opacity-60 group-hover:opacity-100')} aria-hidden />
      <span className="truncate">{t(`nav.${item.key}`)}</span>
    </NavLink>
  )
}

function CollapsedGroup({ group, pathname }: { group: NavGroup; pathname: string }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const active = groupHasActive(group, pathname)
  const Icon = group.icon
  const chipClass = cn(
    'flex size-10 items-center justify-center rounded-xl outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring',
    active ? 'bg-brand-blue/10 text-brand-orange dark:bg-white/10' : 'text-foreground/60 hover:bg-foreground/[0.05] hover:text-foreground',
    open && !active && 'bg-foreground/[0.05] text-foreground',
  )

  const [item] = group.items
  if (group.items.length === 1 && item) {
    return (
      <Tooltip content={t(`nav.${item.key}`)}>
        <NavLink to={item.to} end={item.to === '/'} className={chipClass} aria-label={t(`nav.${item.key}`)}>
          <Icon className="size-[18px]" aria-hidden />
        </NavLink>
      </Tooltip>
    )
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip content={t(`nav.groups.${group.key}`)}>
        <PopoverTrigger asChild>
          <button type="button" className={chipClass} aria-label={t(`nav.groups.${group.key}`)}>
            <Icon className="size-[18px]" aria-hidden />
          </button>
        </PopoverTrigger>
      </Tooltip>
      <PopoverContent side="right" align="start" sideOffset={14} className="w-56 p-1.5">
        <div className="px-2.5 pb-1.5 pt-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
          {t(`nav.groups.${group.key}`)}
        </div>
        <div className="space-y-0.5">
          {group.items.map((item) => (
            <PopoverItemLink key={item.key} item={item} active={itemActive(item, pathname)} onNavigate={() => setOpen(false)} />
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}

export function NavList({ collapsed, onNavigate }: { collapsed?: boolean; onNavigate?: () => void }) {
  const { t } = useTranslation()
  const { can } = useAuth()
  const { pathname } = useLocation()
  const groups = useMemo(() => visibleNav(can), [can])
  const activeKey = useMemo(() => groups.find((g) => groupHasActive(g, pathname))?.key ?? null, [groups, pathname])

  const [open, setOpen] = useState<string | null>(() => activeKey ?? readOpenGroup())
  const [seenPath, setSeenPath] = useState(pathname)

  if (pathname !== seenPath) {
    setSeenPath(pathname)
    if (activeKey && activeKey !== open) {
      persistOpenGroup(activeKey)
      setOpen(activeKey)
    }
  }

  const toggleGroup = (key: string) => {
    const next = open === key ? null : key
    persistOpenGroup(next)
    setOpen(next)
  }

  if (collapsed) {
    return (
      <nav className="flex flex-1 flex-col items-center gap-1 overflow-y-auto px-2 py-4 scrollbar-none" aria-label={t('common.mainNav')}>
        {groups.map((group) => (
          <CollapsedGroup key={group.key} group={group} pathname={pathname} />
        ))}
      </nav>
    )
  }

  return (
    <nav className="flex-1 overflow-y-auto px-3 py-4 scrollbar-none" aria-label={t('common.mainNav')}>
      <div className="mb-2 px-3 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-foreground/40">{t('common.mainNav')}</div>
      <div className="space-y-0.5">
        {groups.map((group) => {
          const active = groupHasActive(group, pathname)
          const Icon = group.icon
          const rowClass = cn(
            'group flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-[13px] font-medium transition-colors',
            active ? 'text-brand-navy dark:text-white' : 'text-foreground/70 hover:bg-foreground/[0.05] hover:text-foreground',
          )
          const iconClass = cn(
            'size-[18px] shrink-0 transition-colors',
            active ? 'text-brand-orange' : 'text-foreground/45 group-hover:text-foreground/90',
          )

          const [item] = group.items
          if (group.items.length === 1 && item) {
            return (
              <NavLink
                key={group.key}
                to={item.to}
                end={item.to === '/'}
                onClick={onNavigate}
                className={cn(rowClass, active && 'bg-brand-blue/10 font-semibold dark:bg-white/10')}
              >
                <Icon className={iconClass} aria-hidden />
                <span className="truncate">{t(`nav.${item.key}`)}</span>
              </NavLink>
            )
          }

          const isOpen = open === group.key
          const panelId = `nav-group-${group.key}`
          return (
            <div key={group.key} className={cn('rounded-xl transition-colors duration-200', isOpen && 'bg-brand-blue/[0.05] ring-1 ring-brand-blue/10 dark:bg-black/20 dark:ring-white/5')}>
              <button
                type="button"
                onClick={() => toggleGroup(group.key)}
                aria-expanded={isOpen}
                aria-controls={panelId}
                className={cn(rowClass, isOpen && !active && 'text-foreground')}
              >
                <Icon className={cn(iconClass, isOpen && !active && 'text-foreground/90')} aria-hidden />
                <span className="min-w-0 flex-1 truncate">{t(`nav.groups.${group.key}`)}</span>
                <ChevronRight
                  className={cn('size-4 shrink-0 text-foreground/40 transition-transform duration-200 group-hover:text-foreground/70', isOpen && 'rotate-90 text-foreground/70')}
                  aria-hidden
                />
              </button>
              <div
                id={panelId}
                className={cn('grid transition-[grid-template-rows] duration-200 ease-out', isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]')}
              >
                <div className="overflow-hidden">
                  <div className="space-y-0.5 px-1 pb-1.5">
                    {group.items.map((sub) => {
                      const subActive = itemActive(sub, pathname)
                      return (
                        <NavLink
                          key={sub.key}
                          to={sub.to}
                          end={sub.to === '/'}
                          onClick={onNavigate}
                          tabIndex={isOpen ? undefined : -1}
                          className={cn(
                            'group/sub relative flex items-center rounded-lg py-[7px] pl-[38px] pr-3 text-[13px] transition-colors',
                            subActive
                              ? 'bg-background font-semibold text-brand-navy shadow-sm ring-1 ring-inset ring-brand-blue/10 dark:bg-white/10 dark:text-white dark:shadow-none dark:ring-0'
                              : 'text-foreground/60 hover:bg-foreground/[0.05] hover:text-foreground',
                          )}
                        >
                          <span
                            className={cn(
                              'absolute left-[14px] top-1/2 size-1.5 -translate-y-1/2 rounded-full transition-colors',
                              subActive ? 'bg-brand-orange ring-[3px] ring-brand-orange/25' : 'bg-foreground/25 group-hover/sub:bg-foreground/50',
                            )}
                            aria-hidden
                          />
                          <span className="truncate">{t(`nav.${sub.key}`)}</span>
                        </NavLink>
                      )
                    })}
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </nav>
  )
}

export function Sidebar({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  const { t } = useTranslation()
  const label = collapsed ? t('common.expandNav') : t('common.collapseNav')
  return (
    <aside
      className={cn(
        'sticky top-0 z-40 hidden h-dvh shrink-0 py-3 pl-3 transition-[width] duration-200 lg:block',
        collapsed ? 'w-[84px]' : 'w-[252px]',
      )}
    >
      <div className="relative flex h-full flex-col rounded-2xl border border-sidebar-border bg-sidebar shadow-[0_1px_2px_color-mix(in_oklab,var(--shadow-soft)_4%,transparent),0_12px_32px_-12px_color-mix(in_oklab,var(--shadow-color)_18%,transparent)] dark:shadow-[0_12px_32px_-12px_color-mix(in_oklab,var(--black)_60%,transparent)]">
      <Brand collapsed={collapsed} inverted />
      <NavList collapsed={collapsed} />
      <Tooltip content={label}>
        <button
          type="button"
          onClick={onToggle}
          aria-label={label}
          className="absolute -right-3 top-11 flex size-6 items-center justify-center rounded-full border border-border bg-background text-muted-foreground shadow-md transition-all hover:scale-110 hover:border-brand-orange hover:bg-brand-orange hover:text-white focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
        >
          <ChevronLeft className={cn('size-3.5 transition-transform duration-200', collapsed && 'rotate-180')} aria-hidden />
        </button>
      </Tooltip>
      </div>
    </aside>
  )
}
